import React, { useCallback, useEffect, useContext, useState } from 'react';
import _ from 'lodash';
import { WizardPage, wizardProps } from 'gw-portals-wizard-react';
import { useDependencies } from 'gw-portals-dependency-react';
import { useAuthentication } from 'gw-digital-auth-react';
import { useValidation } from 'gw-portals-validation-react';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';
import { messages } from 'gw-capability-policychange-common-react';
import { TranslatorContext, IntlContext } from '@jutro/locale';
import { ViewModelForm } from 'gw-portals-viewmodel-react';
import wizardConfig from '../../config/ho-wizard-config.json5';
import metadata from './PolicyChangesPage.metadata.json5';
import styles from '../../HOPolicyChange.module.scss';
import './PolicyChangesPage.messages';
import PortalUtil from '../../../../utils/PortalUtil';
import { DateUtil } from 'gw-portals-util-js';
import { valueTypePropDatatypeMap } from 'gw-components-platform-react/ScheduleItemsComponent/ScheduleItemsUtil';
import {ModalNextProvider } from '@jutro/components';
import { messages as commonMessages } from 'gw-platform-translations';

function PolicyChangesPage(props) {
    const { steps } = wizardConfig;
    const { authHeader } = useAuthentication();
    const translator = useContext(TranslatorContext);
    const { EndorsementService } = useDependencies('EndorsementService');
    const {
        wizardData, currentStepIndex, changeNextSteps, updateWizardData, policyStatus, createUWActivity, updateCreateUWActivity, jumpToStep
    } = props;
    const { submissionVM, selectedData, loadingFirstTime } = wizardData;
    const {dateUtil} = PortalUtil;
    const { initialValidation, isComponentValid, registerComponentValidation } = useValidation(
        'PolicyChangesPage'
    );
    const [isNoPriorPolicy, setIsNoPriorPolicy] = useState();
    const [isScheduledPolicy, setIsScheduledPolicy] = useState();
    const [minEffectiveDate, setMinEffectiveDate] = useState(false);
    const intl = useContext(IntlContext);
    const [prevSelectedOptions, updatePrevSelectedOptions] = useState([]);

    useEffect(()=>{
        if(!minEffectiveDate){
            const minimumEffectiveDate = _.get(submissionVM, 'baseData.minimumEffectiveDate.value')
            setMinEffectiveDate(minimumEffectiveDate);
            /*
            * TMI calculation for min date is the Policy Effective date or Today's date minus 30, whichever greater - so if the Policy Effective date
            * is  Jan  1st, 2021, then the broker can only choose the date from April 5th, 2021 (Today's date minus 30)
            *
            *  const minDate = new Date();
            *  const newMinEffectiveDate = dateUtil.addDaysToLocalDate(dateUtil.toLocalDate(minDate), - 15)
            *  _.set(submissionVM, 'baseData.minimumEffectiveDate.value', newMinEffectiveDate); 
            */
            updateWizardData(wizardData);
        }        
    }, [submissionVM, setMinEffectiveDate, updateWizardData, wizardData])

    const handleChange = useCallback(
        (options) => {
            // if(options.includes('other') && !prevSelectedOptions.includes('other')){
            //     submissionVM.description.value = '';
            // } 
            updatePrevSelectedOptions(options);
            _.forEach(selectedData, (value, key) => {
                _.set(wizardData, `selectedData.${key}`, options.includes(key));
            });
            updateWizardData(wizardData);
        },
        [updateWizardData, wizardData, selectedData, prevSelectedOptions, updatePrevSelectedOptions]
    );

    const onNext = useCallback(async () => {
        const stepMapping = {
            address: {
                "id": "endorsement.ho.directives.templates.ho-valuables.Mailing Address",
                "defaultMessage": "Mailing Address"
            },
            itemDetails: {
                "id": "endorsement.ho.directives.templates.ho-valuables.ItemDetails",
                "defaultMessage": "Item Details"
            },
            itemInfo: {
                "id": "endorsement.ho.directives.templates.ho-valuables.ItemInfo",
                "defaultMessage": "Item Info"
            },
            riskAnalysis: {
                "id": "endorsement.ho.directives.templates.ho-valuables.RiskAnalysis",
                "defaultMessage": "Risk Analysis"
            },
            coverages: {
                "id": "endorsement.ho.directives.templates.ho-valuables.Coverages",
                "defaultMessage": "Coverages"
            },

            location: {
                "id": "endorsement.ho.directives.templates.ho-valuables.Location",
                "defaultMessage": "Location"
            },
            limitDeductibles: {
                "id": "endorsement.ho.directives.templates.ho-valuables.Limit/Deductibles",
                "defaultMessage": "Limit/Deductibles"
            },
            namedInsured: {
                "id": "endorsement.ho.directives.templates.ho-valuables.Named Insured or Additional Named Insured(s)",
                "defaultMessage": "Named Insured or Additional Named Insured(s)"
            },
            packages: {
                "id": "endorsement.ho.directives.templates.ho-valuables.Packages",
                "defaultMessage": "Packages"
            },
            liability: {
                "id": "endorsement.ho.directives.templates.ho-valuables.Liability",
                "defaultMessage": "Liability"
            },
            other: {
                "id": "endorsement.ho.directives.templates.ho-valuables.Other",
                "defaultMessage": "Other"
            },
        }
        let description = submissionVM.description.value;
        if(!selectedData.other) { 
            description = Object.keys(selectedData).filter((k) => {return selectedData[k]}).map((m) => { return 'Amend ' + translator(stepMapping[m])}).join(', ');
        }

        const response = await EndorsementService.checkEffectiveDateIsValid(
            [submissionVM.value.policyNumber, submissionVM.value.baseData.effectiveDate],
            authHeader
        );
        if (response) {
            submissionVM.value = await EndorsementService.loadEndorsementWithEffectiveDate(
                [submissionVM.value.policyNumber, submissionVM.value.baseData.effectiveDate],
                authHeader
            );   
                    
            submissionVM.description.value = description;
            
            submissionVM.value = await EndorsementService.saveEndorsement(
                [submissionVM.value],
                authHeader
            );
        }

        if(!selectedData.other) {
            const newSteps = steps.slice(currentStepIndex + 1, steps.length);
            if (!selectedData.address && !selectedData.namedInsured) {
                newSteps.splice(
                    _.findIndex(newSteps, ({ path }) => path === '/address'),
                    1
                );
            }
            if (!selectedData.itemDetails && !selectedData.packages) {
                newSteps.splice(
                    _.findIndex(newSteps, ({ path }) => path === '/item-details'),
                    1
                );
            }
            if (!selectedData.itemInfo && !selectedData.itemDetails && !selectedData.packages && !selectedData.location) {
                newSteps.splice(
                    _.findIndex(newSteps, ({ path }) => path === '/item-info'),
                    1
                );
            }
            if (!selectedData.riskAnalysis && !selectedData.packages && !selectedData.location) {
                newSteps.splice(
                    _.findIndex(newSteps, ({ path }) => path === '/risk-analysis'),
                    1
                );
            }
            changeNextSteps(newSteps);
        }
        
        return wizardData;
    }, [
        EndorsementService,
        authHeader,
        changeNextSteps,
        selectedData,
        steps,
        submissionVM,
        wizardData,
        currentStepIndex
    ]);

    const getCheckBoxValue = useCallback(() => {
        const result = [];
        _.forEach(selectedData, (value, key) => {
            if (value) {
                result.push(key);
            }
        });
        return result;
    }, [selectedData]);

    const handleEffectiveDateChange = (value) => {
        _.set(submissionVM,'baseData.effectiveDate.value',value)
        const endDate = dateUtil.toDate(_.get(submissionVM, 'baseData.periodEndDate.value'));
        const startDate = dateUtil.toDate(_.get(submissionVM,'baseData.effectiveDate.value'));
        const renewalDateObj = _.get(submissionVM, 'baseData.renewalPeriodEndDate.value')
        if(renewalDateObj){
            const renewalEndDate = dateUtil.toDate(renewalDateObj);
            if(startDate > endDate && startDate < renewalEndDate) {
                ModalNextProvider.showAlert({
                    title: 'Review Effective Date Changes',
                    message: 'Please confirm the policy changes are for Renewal policy',
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: commonMessages.ok
                }).then(() => {
                        updateWizardData(wizardData);
                    }, _.noop);
            } else {
                updateWizardData(wizardData);
            }
        } else {
            updateWizardData(wizardData);
        }
    };
    const minDate = dateUtil.toDate(_.get(submissionVM, 'baseData.minimumEffectiveDate.value'));

    const effectiveDateValue = _.get(submissionVM, 'baseData.effectiveDate.value');
    const startDate = (effectiveDateValue !== undefined && effectiveDateValue !== "") ? dateUtil.toDate(_.get(submissionVM, 'baseData.effectiveDate.value')) : '';
    const minEffDate = dateUtil.toDate(_.get(submissionVM, 'baseData.minimumEffectiveDate.value'));
  
    const today = new Date().setHours(0, 0, 0, 0);
    const endDate = _.get(submissionVM, 'baseData.maximumEffectiveDate.value');
    const renewalEndDate = _.get(submissionVM, 'baseData.renewalPeriodEndDate.value');
    let maxDate = dateUtil.toDate(renewalEndDate || endDate);
    if (maxDate) {
        maxDate = DateUtil.dateDaysAgo(maxDate, 1);
    }

    const getEffectiveDateErrorMessage = () => {
        if (minEffDate > startDate) {
            return [translator(messages.minimumDateErrorMsg, { minEffDate: intl.formatDate(minEffDate, { year: 'numeric', month: 'short', day: 'numeric' }) })];
        }
        if (startDate > maxDate) {
            return [translator(messages.maxDateErrorMsg, { maxDate: intl.formatDate(maxDate, { year: 'numeric', month: 'short', day: 'numeric' }) })];
        }
        return [];
    };

    const editableStatuses = ['Draft', 'Bound', 'Quoted'];

    const transactionStatus = submissionVM?.status?.value?.code;
    const isEffectiveDateReadOnly = !editableStatuses.includes(transactionStatus);

    const overrideProps = {
        '@field': {
            labelPosition: 'left'
        },
        hoPolicyChangeEfectiveDateId: {
            readOnly: isEffectiveDateReadOnly,
            minDate: minEffDate,
            maxDate: maxDate,
            validationMessages: getEffectiveDateErrorMessage(),
            onValueChange: handleEffectiveDateChange
        },
        typeSelector: {
            value: getCheckBoxValue(),
            onValueChange: handleChange
        },
        backDatedEndorsementSection:{
            visible: today > startDate,
        },
        noPriorClaimsOccurred:{
            required: true,
            showRequired: true,
            value: isNoPriorPolicy,            
            onValueChange: (value) => {
                setIsNoPriorPolicy(value);
            }
        },
        /*scheduledEndorsementSection:{
            visible: policyStatus == 'Scheduled'            
        },*/
        scheduledPolicyAccept:{
            required: true,
            showRequired: true,
            value: createUWActivity,            
            onValueChange: (value) => {
                updateCreateUWActivity(value);
            }
        },
        description:{
            visible: _.get(wizardData, 'selectedData')['other'],
            onValueChange: (value, path) => {
                _.set(wizardData, `submissionVM.description.value`, value);
                updateWizardData(wizardData);
            },
            labelPosition: 'left',
            required: !!_.get(wizardData, 'selectedData')['other'],
            showRequired: true
        }
    };

    const resolvers = {
        resolveClassNameMap: styles
    };

    const checkSelectedData = useCallback(() => {
        if (
            selectedData.address === false
            && selectedData.itemDetails === false
            && selectedData.itemInfo === false
            && selectedData.riskAnalysis === false
            && selectedData.coverages === false
            && selectedData.location === false
            && selectedData.limitDeductibles === false
            && selectedData.namedInsured === false
            && selectedData.liability === false
            && selectedData.packages === false
            && selectedData.other === false          
        ) {
            return false;
        }
        return true;
    }, [selectedData]);

    useEffect(() => {
        if(submissionVM.value.status === 'Quoted' && submissionVM.value.description === 'Renewal Policy Change' && loadingFirstTime){
            submissionVM.value.description = '';
            _.set(wizardData, 'loadingFirstTime', false);
            updateWizardData(wizardData);
            navigateToCoverages();
        } else if(submissionVM.value.status === 'Quoted' && loadingFirstTime){
            _.set(wizardData, 'loadingFirstTime', false);
            updateWizardData(wizardData);
            navigateToCoverages();
        }  else {
            registerComponentValidation(checkSelectedData);
        }
    }, [checkSelectedData, registerComponentValidation]);

    const navigateToCoverages = useCallback(() => {
        const indexOfGeneralCoverages = _.findIndex(
            steps,
            ({ path }) => path === '/quotes'
        );
        jumpToStep(indexOfGeneralCoverages);
    }, [jumpToStep, steps]);

    return (
        <WizardPage
            onNext={onNext}
            disableNext={!isComponentValid || (today > startDate && !isNoPriorPolicy) || (minEffDate > startDate || startDate > maxDate)}
            skipWhen={initialValidation}
            showPrevious={false}
            cancelLabel={translator(appConfig.persona === 'policyholder' ? messages.cancelAllChanges : messages.cancel)}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={wizardData}
                overrideProps={overrideProps}
                classNameMap={resolvers.resolveClassNameMap}
                onModelChange={updateWizardData}
            />
        </WizardPage>
    );
}

PolicyChangesPage.propTypes = wizardProps;

export default PolicyChangesPage;
